import { useCallback, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Target, Pencil } from "lucide-react";
import { SlaChangeLog } from "@/components/SlaChangeLog";

const FIELDS = [["warning_minutes", "Warning"], ["overdue_minutes", "Target"], ["critical_minutes", "Critical"]];

export const SlaTargetsPanel = ({ store }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({});
  const [note, setNote] = useState("");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);

  const load = useCallback(() => {
    api.get(`/sla/effective?store_id=${store.id}`).then((r) => setData(r.data)).catch(() => {});
  }, [store.id]);
  useEffect(() => { load(); }, [load]);

  const startEdit = () => { setForm(JSON.parse(JSON.stringify(data.stages))); setNote(""); setOpen(true); };
  const upd = (st, f, v) => setForm((s) => ({ ...s, [st]: { ...s[st], [f]: Number(v) } }));

  const save = async () => {
    setSaving(true);
    try {
      const items = Object.entries(form).map(([stage, v]) => ({ stage, ...v }));
      await api.post("/sla/proposals", { store_id: store.id, items, note });
      toast.success("New targets are live for this store. Your company admin can undo them.");
      setOpen(false); load(); setRefreshKey((k) => k + 1);
    } catch (e) { toast.error(apiError(e)); }
    setSaving(false);
  };

  if (!data) return null;
  return (
    <div className="mt-6 space-y-4" data-testid="sla-targets-panel">
      <div className="bg-card border border-border">
        <div className="px-4 py-3 border-b border-border flex items-center gap-2">
          <Target size={18} className="text-alert-green" />
          <span className="font-display text-xl font-bold uppercase tracking-wide">Service Targets — {store.store_name}</span>
          <span className="text-xs text-muted-foreground ml-2" data-testid="sla-source">{data.uses_company_default_sla ? "Company defaults" : "Store-specific"}</span>
          <Button size="sm" variant="outline" className="ml-auto gap-1" onClick={startEdit} data-testid="sla-edit-btn"><Pencil size={14} /> Change Targets</Button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-3">
          {Object.entries(data.stages).map(([st, v]) => (
            <div key={st} className="bg-secondary/40 border border-border p-3" data-testid={`sla-current-${st}`}>
              <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{data.labels[st]}</div>
              <div className="font-display text-3xl font-extrabold leading-none mt-1">{v.overdue_minutes}<span className="text-sm text-muted-foreground">m</span></div>
              <div className="text-[10px] text-muted-foreground mt-1 font-mono-t">warn {v.warning_minutes} · crit {v.critical_minutes}</div>
            </div>
          ))}
        </div>
      </div>
      <SlaChangeLog storeId={store.id} canUndo={false} refreshKey={refreshKey} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-border max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-2xl">Change Targets — {store.store_name}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">Changes apply immediately to this store. Your company admin is able to undo them.</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2">Stage</th>{FIELDS.map(([f, l]) => <th key={f} className="py-2">{l} (min)</th>)}</tr></thead>
              <tbody>
                {Object.entries(form).map(([st, v]) => (
                  <tr key={st} className="border-b border-border">
                    <td className="py-2 pr-2 font-medium">{data.labels[st]}</td>
                    {FIELDS.map(([f]) => (
                      <td key={f} className="py-2 pr-2">
                        <Input type="number" min={1} className="w-20 h-8" value={v[f]} onChange={(e) => upd(st, f, e.target.value)} data-testid={`sla-input-${st}-${f}`} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Textarea placeholder="Reason for the change (optional), e.g. short-staffed on Saturdays" value={note} onChange={(e) => setNote(e.target.value)} data-testid="sla-note" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="sla-save-btn">Apply Targets</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SlaTargetsPanel;
